import { useCallback, useEffect, useState } from 'react';

import { insights as insightsApi } from '@/helpers/ApiHelper';
import type { DashboardData } from '@/types/insights';

const REFRESH_INTERVAL_MS = 60_000;

interface UseInsightsDashboardResult {
  data: DashboardData | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useInsightsDashboard(): UseInsightsDashboardResult {
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await insightsApi.dashboard();
      setData(result);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    insightsApi
      .dashboard()
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    // 60-second refresh interval.
    const intervalId = setInterval(() => {
      if (!cancelled) {
        insightsApi
          .dashboard()
          .then((result) => {
            if (!cancelled) {
              setData(result);
              setError(null);
            }
          })
          .catch((e: unknown) => {
            if (!cancelled) {
              setError(e instanceof Error ? e : new Error(String(e)));
            }
          });
      }
    }, REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, []);

  return { data, isLoading, error, refetch: fetchData };
}
